import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { api, errMsg } from "../lib/api";

export const PERMISSIONS = [
  ["deposit", "Add deposit"], ["withdraw", "Add withdraw"], ["edit", "Edit entries"], ["delete", "Delete entries"],
  ["dashboard", "View dashboard totals"], ["export", "Export PDF / Excel"], ["share", "WhatsApp share"],
];

const BLANK = { name: "", email: "", password: "", permissions: ["deposit", "withdraw"], site_ids: [], branch_ids: [] };

const CheckList = ({ items, selected, onToggle, prefix }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
    {items.map(([id, label]) => (
      <label key={id} className="flex items-center gap-2 text-sm border border-line px-3 py-2 cursor-pointer hover:bg-hover">
        <Checkbox checked={selected.includes(id)} onCheckedChange={() => onToggle(id)} data-testid={`${prefix}-${id}`} />
        <span className="truncate">{label}</span>
      </label>
    ))}
  </div>
);

export const StaffDialog = ({ open, staff, sites, onClose, onSubmit }) => {
  const [f, setF] = useState(BLANK);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    setError("");
    if (open) {
      setF(staff ? { ...BLANK, ...staff, password: "" } : BLANK);
      api.get("/branches").then((r) => setBranches(r.data)).catch(() => setBranches([]));
    }
  }, [open, staff]);

  const toggleSite = (id) => setF((x) => x.site_ids.includes(id)
    ? { ...x, site_ids: x.site_ids.filter((y) => y !== id), branch_ids: x.branch_ids.filter((b) => branches.find((br) => br.id === b)?.site_id !== id) }
    : { ...x, site_ids: [...x.site_ids, id] });

  const toggle = (key) => (id) => setF((x) => ({ ...x, [key]: x[key].includes(id) ? x[key].filter((y) => y !== id) : [...x[key], id] }));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await onSubmit({ name: f.name, email: f.email, password: f.password || null, permissions: f.permissions, site_ids: f.site_ids, branch_ids: f.branch_ids });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="rounded-none sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="staff-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader><DialogTitle className="font-display">{staff ? "Edit staff" : "Add staff"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Name</Label><Input required value={f.name} onChange={set("name")} className="mt-1 rounded-none" data-testid="staff-name-input" /></div>
            <div><Label>Email</Label><Input required type="email" value={f.email} onChange={set("email")} className="mt-1 rounded-none" data-testid="staff-email-input" /></div>
          </div>
          <div>
            <Label>{staff ? "New password (leave empty to keep)" : "Password"}</Label>
            <Input type="text" required={!staff} minLength={6} value={f.password} onChange={set("password")} className="mt-1 rounded-none font-mono" data-testid="staff-password-input" />
          </div>
          <div>
            <Label>Books this staff can see</Label>
            {sites.length ? <CheckList items={sites.map((s) => [s.id, s.name])} selected={f.site_ids} onToggle={toggleSite} prefix="staff-site" />
              : <p className="text-sm text-muted-c mt-1">Add a book first.</p>}
          </div>
          {f.site_ids.map((sid) => {
            const list = branches.filter((b) => b.site_id === sid);
            const picked = list.filter((b) => f.branch_ids.includes(b.id)).length;
            return (
              <div key={sid} className="border-l-2 border-brand pl-3" data-testid={`staff-branches-${sid}`}>
                <Label>Branches in {sites.find((s) => s.id === sid)?.name}</Label>
                <p className="text-xs text-muted-c">{picked ? `Only ${picked} selected branch${picked > 1 ? "es" : ""}` : "All branches (tick to limit)"}</p>
                {list.length ? <CheckList items={list.map((b) => [b.id, b.name])} selected={f.branch_ids} onToggle={toggle("branch_ids")} prefix="staff-branch" />
                  : <p className="text-xs text-muted-c mt-1">No branches yet</p>}
              </div>
            );
          })}
          <div>
            <Label>Permissions</Label>
            <CheckList items={PERMISSIONS} selected={f.permissions} onToggle={toggle("permissions")} prefix="staff-perm" />
          </div>
          {error && <p className="text-sm text-neg" data-testid="staff-error">{error}</p>}
          <DialogFooter>
            <Button type="submit" disabled={busy} className="rounded-none w-full bg-brand hover:bg-brand/90 text-onbrand" data-testid="staff-save-button">
              {busy ? "Saving…" : staff ? "Update staff" : "Create staff"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
